import { LitElement, html, css } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { Plus, ChevronDown, Upload, Mic, Play, Square, Pencil } from 'lucide';
import { lucideIcon } from '../icon.js';
import { api } from '../api.js';
import { confirmDialog } from '../utils/confirm-dialog.js';
import { meetGeluidDuur, scangeluidProbleem, MAX_SCANGELUID_MS } from '../utils/geluid-duur.js';
import { maakScangeluidVanOpname } from '../utils/geluid-bewerk.js';

/**
 * Ingebouwde scangeluiden, voor alle bedrijven. Zelfde sleutels en namen als in de veldapp
 * (mv-field-recorder/src/services/ingebouwdeScanGeluiden.ts); een test bewaakt dat.
 */
export const INGEBOUWD = [
  { id: 'standaard', naam: 'Scan piep', url: '/sounds/scan-piep.mp3' },
  { id: 'ingebouwd:level-up', naam: 'Level up', url: '/sounds/level-up.m4a' },
  { id: 'ingebouwd:goed-resultaat', naam: 'Goed resultaat', url: '/sounds/goed-resultaat.m4a' },
  { id: 'ingebouwd:ok-lets-go', naam: 'Ok, let’s go', url: '/sounds/ok-lets-go.m4a' },
];
const NIEUW_ID = 'nieuw';
const duurTekst = (ms) => (ms == null ? '' : `${(ms / 1000).toFixed(1).replace('.', ',')} s`);
/** 'mp3', 'm4a', 'wav', 'ogg' -- of 'opname' voor wat in de browser met de microfoon is opgenomen (webm). */
function soortVan(contentType) {
  const t = String(contentType || '').toLowerCase();
  if (/mpeg|mp3/.test(t)) return 'mp3';
  if (/mp4|m4a|aac/.test(t)) return 'm4a';
  if (/wav/.test(t)) return 'wav';
  if (/ogg/.test(t)) return 'ogg';
  if (/webm/.test(t)) return 'opname';
  return '';
}
/** 24 pieken (0..1) uit een geluidsbestand, voor de golfvorm op een tegel. */
async function berekenGolf(blob, aantal = 24) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  const ctx = new Ctx();
  try {
    const buf = await ctx.decodeAudioData(await blob.arrayBuffer());
    const data = buf.getChannelData(0);
    const stap = Math.max(1, Math.floor(data.length / aantal));
    const pieken = [];
    for (let i = 0; i < aantal; i++) {
      let max = 0;
      for (let j = i * stap; j < Math.min(data.length, (i + 1) * stap); j++) max = Math.max(max, Math.abs(data[j]));
      pieken.push(max);
    }
    const top = Math.max(...pieken) || 1;
    // Wortel: zachte stukken blijven zichtbaar, zodat de golfvorm niet uit losse streepjes bestaat.
    return { pieken: pieken.map((p) => Math.sqrt(p / top)), duurMs: Math.round(buf.duration * 1000) };
  } catch (_) {
    return null;
  } finally {
    ctx.close?.();
  }
}

/**
 * Scangeluiden van het bedrijf: de ingebouwde geluiden plus wat het bedrijf zelf uploadt of
 * opneemt. Staat op /settings/recorder (Recorder app), onder de instructies: beide gaan over
 * wat de veldapp doet. `canEdit` komt van de pagina; de server dwingt het ook af.
 */
export class ScanSoundsCard extends LitElement {
  static properties = {
    canEdit: { type: Boolean },
    _geluiden: { state: true },
    _nieuwGeluid: { state: true },   // { file, naam, duurMs } terwijl je er één toevoegt
    _geluidFout: { state: true },
    _geluidBezig: { state: true },
    _opnameMs: { state: true },      // != null terwijl er via de microfoon wordt opgenomen
    _opnameNiveaus: { state: true }, // geluidsniveau per stukje van de opname (live balkjes)
    _aftellen: { state: true },      // 3, 2, 1 vóór de opname begint (microfoon warmt op); null = niet
    _menuOpen: { state: true },      // uitklapmenu van 'Audio toevoegen' (zelfde als 'Nieuwe boring')
    _golven: { state: true },        // geluid-id → piekhoogtes (0..1) voor de golfvorm op de tegel
    _speelt: { state: true },        // id van het geluid dat nu klinkt
    _ingebouwdMs: { state: true },   // id → lengte van de ingebouwde geluiden (gemeten)
    _hernoem: { state: true },       // { id, naam } terwijl de naam van een geluid wordt aangepast
  };

  constructor() {
    super();
    this.canEdit = false;
    this._geluiden = [];
    this._nieuwGeluid = null;
    this._geluidFout = null;
    this._geluidBezig = false;
    this._opnameMs = null;
    this._opnameNiveaus = [];
    this._aftellen = null;
    this._opname = null;
    this._menuOpen = false;
    this._menuPos = null;
    this._golven = {};
    this._speelt = null;
    this._audio = null;
  }

  connectedCallback() { super.connectedCallback(); this._load(); }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._stopOpname(true);
    this._audio?.pause();
  }

  async _load() {
    this._geluiden = await api.getCompanySounds().catch(() => []);
    this._laadGolven();
  }

  _kiesGeluid() { this.renderRoot.querySelector('#geluid-input')?.click(); }

  async _onGeluid(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    this._geluidFout = null;
    const duurMs = await meetGeluidDuur(file);
    const probleem = scangeluidProbleem({ type: file.type, size: file.size, duurMs });
    if (probleem) { this._geluidFout = probleem; this._nieuwGeluid = null; return; }
    this._nieuwGeluid = { file, naam: file.name.replace(/\.[^.]+$/, '').slice(0, 60), duurMs, bron: 'bestand', pieken: null };
    berekenGolf(file).then((g) => { if (g && this._nieuwGeluid?.file === file) this._nieuwGeluid = { ...this._nieuwGeluid, pieken: g.pieken }; });
  }

  /** 'Opname 2': de eerste vrije naam, zodat twee opnames niet allebei 'Opname' heten. */
  _volgendeOpnameNaam() {
    const namen = new Set(this._geluiden.map((g) => g.name));
    for (let n = 1; ; n++) if (!namen.has(`Opname ${n}`)) return `Opname ${n}`;
  }

  /*
   * Opnemen met de microfoon van de laptop. Stopt vanzelf vlak voor de 3 seconden.
   *
   * Zonder de filters van de browser (ruisonderdrukking, echo-onderdrukking, automatisch volume):
   * die zijn voor gesprekken, en filterden een kort woord als "test" deels weg. Na afloop knipt
   * maakScangeluidVanOpname de stilte voor en na eraf en zet het geluid op sterkte. Tijdens het
   * opnemen tonen we het niveau als balkjes: zo zie je meteen of de microfoon je hoort.
   *
   * Eerst 3 seconden aftellen: de microfoon staat dan al aan en is opgewarmd als de opname begint
   * (een microfoon die net aangaat mist vaak de eerste halve seconde), en je weet precies wanneer
   * je moet spreken.
   */
  async _startOpname() {
    if (this._opname) return; // er loopt al een opname (of aftelling)
    this._geluidFout = null;
    this._nieuwGeluid = null;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      this._geluidFout = 'Opnemen kan niet in deze browser. Kies een bestand.';
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
    } catch (_) {
      this._geluidFout = 'Geen toegang tot de microfoon. Sta dit toe in de browser (slotje in de adresbalk).';
      return;
    }
    const type = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']
      .find((t) => MediaRecorder.isTypeSupported?.(t)) || '';
    const recorder = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
    const stukken = [];
    // Niveaumeter: per 125 ms het hoogste niveau = 24 balkjes voor 3 seconden.
    let meter = null;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      ctx.createMediaStreamSource(stream).connect(analyser);
      meter = { ctx, analyser, data: new Float32Array(analyser.fftSize), piek: 0 };
    } catch (_) { /* geen balkjes, opnemen gaat gewoon door */ }
    recorder.ondataavailable = (e) => { if (e.data?.size) stukken.push(e.data); };
    recorder.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      meter?.ctx.close?.();
      const geannuleerd = this._opname?.geannuleerd;
      const niveaus = this._opnameNiveaus;
      this._opname = null;
      this._opnameMs = null;
      if (geannuleerd || !stukken.length) return;
      const mime = (recorder.mimeType || type || 'audio/webm').split(';')[0];
      const ext = mime.includes('mp4') ? 'm4a' : mime.includes('ogg') ? 'ogg' : 'webm';
      const ruw = new File(stukken, `opname.${ext}`, { type: mime });
      const bewerkt = await maakScangeluidVanOpname(ruw);
      if (bewerkt?.leeg) {
        this._geluidFout = 'Er is niets opgenomen. Controleer of de juiste microfoon aan staat en niet gedempt is, en probeer het opnieuw.';
        return;
      }
      const file = bewerkt?.file || ruw;
      const duurMs = bewerkt?.duurMs ?? await meetGeluidDuur(file);
      const probleem = scangeluidProbleem({ type: file.type, size: file.size, duurMs });
      if (probleem) { this._geluidFout = probleem; return; }
      this._nieuwGeluid = { file, naam: this._volgendeOpnameNaam(), duurMs, bron: 'opname', pieken: null };
      const golf = await berekenGolf(file);
      if (this._nieuwGeluid?.file === file) this._nieuwGeluid = { ...this._nieuwGeluid, pieken: golf ? golf.pieken : niveaus };
    };
    this._opname = { recorder, stream, meter, geannuleerd: false, teller: null, einde: null, aftel: null };
    this._opnameNiveaus = [];
    this._aftellen = 3;
    await new Promise((verder) => {
      this._opname.aftel = setInterval(() => {
        if (!this._opname || this._opname.geannuleerd) { clearInterval(this._opname?.aftel); verder(); return; }
        this._aftellen -= 1;
        if (this._aftellen <= 0) { clearInterval(this._opname.aftel); verder(); }
      }, 1000);
    });
    this._aftellen = null;
    if (!this._opname || this._opname.geannuleerd) {
      // Geannuleerd tijdens het aftellen: microfoon uit, niets opnemen.
      stream.getTracks().forEach((t) => t.stop());
      meter?.ctx.close?.();
      this._opname = null;
      return;
    }
    const begin = Date.now();
    this._opnameMs = 0;
    Object.assign(this._opname, {
      teller: setInterval(() => {
        this._opnameMs = Date.now() - begin;
        if (!meter) return;
        meter.analyser.getFloatTimeDomainData(meter.data);
        for (const v of meter.data) meter.piek = Math.max(meter.piek, Math.abs(v));
        const vak = Math.floor(this._opnameMs / 125);
        if (vak >= this._opnameNiveaus.length) {
          // Wortel + versterking: spreken op normale afstand moet duidelijk uitslaan.
          this._opnameNiveaus = [...this._opnameNiveaus, Math.min(1, Math.sqrt(meter.piek) * 1.6)];
          meter.piek = 0;
        }
      }, 40),
      einde: setTimeout(() => this._stopOpname(), MAX_SCANGELUID_MS - 50),
    });
    recorder.start(100);
  }

  _stopOpname(annuleren = false) {
    const o = this._opname;
    if (!o) return;
    clearInterval(o.teller);
    clearTimeout(o.einde);
    // Tijdens het aftellen is er nog niets opgenomen: stoppen = annuleren.
    if (this._aftellen != null) { o.geannuleerd = true; return; }
    o.geannuleerd = annuleren;
    if (o.recorder.state !== 'inactive') o.recorder.stop();
  }

  _speel(url, id = null) {
    this._audio?.pause();
    try {
      const a = new Audio(url);
      this._audio = a;
      this._speelt = id;
      const klaar = () => { if (this._audio === a) { this._speelt = null; this._audio = null; } };
      a.onended = klaar; a.onerror = klaar;
      a.play().catch(klaar);
    } catch (_) { this._speelt = null; /* afspelen is een voorbeeld, geen functie */ }
  }

  /** Afspelen, of stoppen als dit geluid al klinkt. */
  async _speelOpgeslagen(id) {
    if (this._speelt === id) { this._audio?.pause(); this._speelt = null; this._audio = null; return; }
    if (id === NIEUW_ID) { if (this._nieuwGeluid) this._speel(URL.createObjectURL(this._nieuwGeluid.file), id); return; }
    const ingebouwd = INGEBOUWD.find((x) => x.id === id);
    if (ingebouwd) { this._speel(ingebouwd.url, id); return; }
    try { this._speel(URL.createObjectURL(await api.fetchCompanySoundBlob(id)), id); }
    catch (_) { this._geluidFout = 'Afspelen mislukt'; }
  }

  /*
   * De golfvorm op elke tegel is het echte geluid: per geluid 24 pieken. De bestanden zijn klein
   * (max 600 KB), dus even ophalen en decoderen is geen punt. Lukt het niet, dan blijft de tegel
   * gewoon zonder golfvorm.
   */
  async _laadGolven() {
    const bronnen = [...INGEBOUWD.map((x) => ({ id: x.id, haal: () => fetch(x.url).then((r) => r.blob()) })),
      ...this._geluiden.map((g) => ({ id: g.id, haal: () => api.fetchCompanySoundBlob(g.id) }))];
    for (const b of bronnen) {
      if (this._golven[b.id]) continue;
      try {
        const golf = await berekenGolf(await b.haal());
        if (!golf) continue;
        this._golven = { ...this._golven, [b.id]: golf.pieken };
        if (INGEBOUWD.some((x) => x.id === b.id)) this._ingebouwdMs = { ...this._ingebouwdMs, [b.id]: golf.duurMs };
      } catch (_) { /* geen golfvorm */ }
    }
  }

  async _voegGeluidToe() {
    const n = this._nieuwGeluid;
    if (!n) return;
    const naam = (n.naam || '').trim() || 'Geluid';
    this._geluidBezig = true; this._geluidFout = null;
    try {
      const nieuw = await api.uploadCompanySound(n.file, naam, n.duurMs);
      this._geluiden = [...this._geluiden, nieuw];
      this._nieuwGeluid = null;
      this._laadGolven();
    } catch (e) {
      this._geluidFout = e?.message || 'Toevoegen mislukt';
    } finally {
      this._geluidBezig = false;
    }
  }

  _startHernoem(g) {
    this._geluidFout = null;
    this._hernoem = { id: g.id, naam: g.name };
    this.updateComplete.then(() => {
      const i = this.renderRoot.querySelector('.tegel-naam-invoer');
      i?.focus(); i?.select();
    });
  }

  async _bewaarHernoem() {
    const h = this._hernoem;
    if (!h) return;
    this._hernoem = null;
    const g = this._geluiden.find((x) => x.id === h.id);
    const naam = (h.naam || '').trim().slice(0, 60);
    if (!g || !naam || naam === g.name) return;
    const oud = g.name;
    this._geluiden = this._geluiden.map((x) => (x.id === h.id ? { ...x, name: naam } : x));
    try {
      await api.renameCompanySound(h.id, naam);
    } catch (e) {
      this._geluiden = this._geluiden.map((x) => (x.id === h.id ? { ...x, name: oud } : x));
      this._geluidFout = e?.message || 'Naam wijzigen mislukt';
    }
  }

  async _verwijderGeluid(g) {
    const ok = await confirmDialog({
      title: 'Scangeluid verwijderen',
      message: `"${g.name}" verwijderen? Wie dit geluid gekozen had, hoort daarna de Scan piep.`,
      confirmLabel: 'Verwijderen',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteCompanySound(g.id);
      this._geluiden = this._geluiden.filter((x) => x.id !== g.id);
    } catch (e) {
      this._geluidFout = e?.message || 'Verwijderen mislukt';
    }
  }

  /** 'Audio toevoegen' met uitklapmenu -- hetzelfde patroon als 'Nieuwe boring' in een project. */
  _renderAudioToevoegen() {
    const item = (icoon, tekst, actie, eerste) => html`
      <button style="width:100%; display:flex; align-items:center; gap:8px; padding:9px 12px; background:none; border:none; ${eerste ? '' : 'border-top:1px solid var(--mv-border);'} font-size:0.82rem; font-weight:600; color:var(--mv-text); cursor:pointer; text-align:left;"
        @mouseenter=${(e) => e.currentTarget.style.background = 'var(--mv-surface-alt,#F3F4F6)'}
        @mouseleave=${(e) => e.currentTarget.style.background = 'none'}
        @click=${() => { this._menuOpen = false; actie(); }}>
        ${unsafeSVG(lucideIcon(icoon, 15))} ${tekst}
      </button>`;
    return html`
      <div style="display:inline-block;">
        <button class="upload" style="height:36px; font-size:0.85rem; padding:0 14px; display:flex; align-items:center; gap:6px;"
          @click=${(e) => {
            e.stopPropagation();
            if (this._menuOpen) { this._menuOpen = false; return; }
            const rect = e.currentTarget.getBoundingClientRect();
            this._menuPos = { top: rect.bottom + 6, left: rect.left };
            this._geluidFout = null;
            this._menuOpen = true;
          }}>
          ${unsafeSVG(lucideIcon(Plus, 14))} Audio toevoegen ${unsafeSVG(lucideIcon(ChevronDown, 13))}
        </button>
        ${this._menuOpen ? html`
          <div @click=${() => this._menuOpen = false} style="position:fixed; inset:0; z-index:1000;"></div>
          <div style="position:fixed; top:${this._menuPos?.top ?? 0}px; left:${this._menuPos?.left ?? 0}px; z-index:1001; min-width:220px; background:var(--mv-surface); border:1px solid var(--mv-border); border-radius:8px; box-shadow:0 8px 24px rgba(0,0,0,0.12); overflow:hidden;">
            ${item(Upload, 'Bestand uploaden', () => this._kiesGeluid(), true)}
            ${item(Mic, 'Opnemen met microfoon', () => this._startOpname(), false)}
          </div>
        ` : ''}
      </div>`;
  }

  _renderTegel({ id, naam, duurMs, soort, herkomst, verwijder, hernoem }) {
    const bewerkt = this._hernoem?.id === id;
    const pieken = this._golven[id] || Array(24).fill(0.18);
    const speelt = this._speelt === id;
    return html`
      <div class="tegel ${speelt ? 'speelt' : ''}">
        <div class="tegel-kop">
          <button class="rond" @click=${() => this._speelOpgeslagen(id)} aria-label=${speelt ? 'Stoppen' : 'Afspelen'}>
            ${unsafeSVG(lucideIcon(speelt ? Square : Play, 13))}
          </button>
          <div class="tegel-tekst">
            ${bewerkt ? html`
              <input class="tegel-naam-invoer" maxlength="60" .value=${this._hernoem.naam}
                @input=${(e) => { this._hernoem = { ...this._hernoem, naam: e.target.value }; }}
                @keydown=${(e) => { if (e.key === 'Enter') this._bewaarHernoem(); if (e.key === 'Escape') { this._hernoem = null; } }}
                @blur=${() => this._bewaarHernoem()}>
            ` : html`
              <div class="tegel-naam-rij">
                <div class="tegel-naam" title=${naam}>${naam}</div>
                ${hernoem ? html`<button class="potlood" title="Naam wijzigen" aria-label="Naam wijzigen" @click=${hernoem}>${unsafeSVG(lucideIcon(Pencil, 12))}</button>` : ''}
              </div>`}
            <div class="tegel-meta">${duurTekst(duurMs)}${soort ? ` · ${soort}` : ''}</div>
          </div>
        </div>
        <div class="golf" aria-hidden="true">${pieken.map((h) => html`<span style="height:${Math.max(22, Math.round(h * 100))}%"></span>`)}</div>
        <div class="tegel-voet">
          <span>${herkomst}</span>
          ${verwijder ? html`<button class="link-rood" @click=${verwijder}>Verwijderen</button>` : ''}
        </div>
      </div>`;
  }

  /** Tijdens het opnemen: rode tegel met teller, live balkjes en Stoppen. */
  _renderOpnameTegel() {
    if (this._aftellen != null) {
      return html`
        <div class="tegel opnemen aftellen">
          <div class="aftel-getal">${this._aftellen}</div>
          <div class="tegel-meta">Opname begint over ${this._aftellen} s</div>
          <div class="tegel-voet"><span></span><button class="link-rood" @click=${() => this._stopOpname(true)}>Annuleren</button></div>
        </div>`;
    }
    const n = this._opnameNiveaus;
    const balkjes = Array.from({ length: 24 }, (_, i) => n[i]);
    return html`
      <div class="tegel opnemen">
        <div class="tegel-kop">
          <button class="rond rec-knop" @click=${() => this._stopOpname()} aria-label="Stoppen"><span></span></button>
          <div class="tegel-tekst">
            <div class="tegel-naam">Opnemen…</div>
            <div class="tegel-meta">${duurTekst(this._opnameMs).replace(' s', '')} / ${duurTekst(MAX_SCANGELUID_MS)}</div>
          </div>
        </div>
        <div class="golf">${balkjes.map((h) => h == null
          ? html`<span class="leeg"></span>`
          : html`<span style="height:${Math.max(14, Math.round(h * 100))}%"></span>`)}</div>
        <div class="tegel-voet"><span></span><button class="link-rood" @click=${() => this._stopOpname()}>Stoppen</button></div>
      </div>`;
  }

  /** Na het opnemen of kiezen: groene stippeltegel met naamveld, voorbeeld en Opnieuw/Annuleren/Toevoegen. */
  _renderNieuwTegel() {
    const n = this._nieuwGeluid;
    const speelt = this._speelt === NIEUW_ID;
    const pieken = n.pieken || Array(24).fill(0.18);
    return html`
      <div class="tegel nieuw ${speelt ? 'speelt' : ''}">
        <div class="tegel-kop">
          <button class="rond" @click=${() => this._speelOpgeslagen(NIEUW_ID)} aria-label=${speelt ? 'Stoppen' : 'Afspelen'}>
            ${unsafeSVG(lucideIcon(speelt ? Square : Play, 13))}
          </button>
          <div class="tegel-tekst">
            <input class="nieuw-naam" .value=${n.naam} maxlength="60" placeholder="Naam" aria-label="Naam van het geluid"
              @input=${(e) => { this._nieuwGeluid = { ...this._nieuwGeluid, naam: e.target.value }; }}
              @keydown=${(e) => { if (e.key === 'Enter') this._voegGeluidToe(); }}>
            <div class="tegel-meta">${duurTekst(n.duurMs)} · ${n.bron === 'opname' ? 'nieuwe opname' : 'nieuw bestand'}</div>
          </div>
        </div>
        <div class="golf" aria-hidden="true">${pieken.map((h) => html`<span style="height:${Math.max(22, Math.round(h * 100))}%"></span>`)}</div>
        <div class="tegel-voet nieuw-acties">
          <button class="link" ?disabled=${this._geluidBezig} @click=${() => (n.bron === 'opname' ? this._startOpname() : this._kiesGeluid())}>Opnieuw</button>
          <button class="link" ?disabled=${this._geluidBezig} @click=${() => { this._nieuwGeluid = null; }}>Annuleren</button>
          <button class="link groen" ?disabled=${this._geluidBezig} @click=${this._voegGeluidToe}>${this._geluidBezig ? 'Toevoegen…' : 'Toevoegen'}</button>
        </div>
      </div>`;
  }

  _renderGeluiden() {
    return html`
      <section class="kaart">
        <div class="kaart-kop">
          <p class="hint" style="margin:0;">
            Klinkt in de veldapp bij een geslaagde barcodescan. Medewerkers kiezen hun geluid via
            Instellingen → Scangeluid.
          </p>
          ${this.canEdit ? this._renderAudioToevoegen() : ''}
        </div>

        <div class="tegels">
          ${INGEBOUWD.map((x) => this._renderTegel({ id: x.id, naam: x.naam, duurMs: this._ingebouwdMs?.[x.id], soort: '', herkomst: 'Ingebouwd' }))}
          ${this._geluiden.map((g) => this._renderTegel({
            id: g.id, naam: g.name, duurMs: g.durationMs, soort: soortVan(g.contentType), herkomst: 'Geüpload',
            verwijder: this.canEdit ? () => this._verwijderGeluid(g) : null,
            hernoem: this.canEdit ? () => this._startHernoem(g) : null,
          }))}
          ${this._opnameMs != null || this._aftellen != null ? this._renderOpnameTegel() : this._nieuwGeluid ? this._renderNieuwTegel() : ''}
        </div>

        ${this.canEdit ? html`
          <input id="geluid-input" type="file" accept="audio/*" @change=${this._onGeluid} style="display:none;">
          ${this._geluidFout ? html`<p class="err" style="margin:0.75rem 0 0;">${this._geluidFout}</p>` : ''}
          <p class="hint voetnoot">mp3, m4a, wav of ogg · maximaal 3 seconden en 600 KB. Een opname stopt vanzelf na 3 seconden.</p>
        ` : html`<p class="muted voetnoot">Alleen een beheerder of eigenaar kan scangeluiden toevoegen.</p>`}
      </section>
    `;
  }

  render() { return this._renderGeluiden(); }

  static styles = css`
    :host { display: block; }
    .kaart {
      background: var(--mv-surface, #fff); border: 1px solid var(--mv-border); border-radius: 12px;
      padding: 20px; margin-bottom: 1.5rem; box-sizing: border-box;
    }
    .kaart-kop { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 16px; }
    .kaart-kop > p:first-child { max-width: 440px; }
    .hint { color: var(--mv-text-muted); font-size: 0.8rem; line-height: 1.45; margin: 0.6rem 0 0; }
    .voetnoot { margin-top: 16px; font-size: 0.75rem; }
    .muted { color: var(--mv-text-muted); }
    .err { color: #DC2626; font-weight: 600; font-size: 0.85rem; }
    button.upload {
      background: var(--mv-primary); color: #fff; border: none; border-radius: 6px;
      padding: 8px 14px; font-weight: 700; font-size: 0.85rem; cursor: pointer; font-family: inherit;
    }
    button:disabled { opacity: 0.55; cursor: default; }

    .tegels { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
    @media (max-width: 640px) { .tegels { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
    .tegel {
      border: 1px solid var(--mv-border); border-radius: 8px; padding: 12px; display: flex; flex-direction: column; gap: 10px;
      background: var(--mv-surface, #fff); transition: border-color .15s;
    }
    .tegel.speelt { border-color: var(--mv-primary); }
    .tegel-kop { display: flex; align-items: center; gap: 10px; min-width: 0; }
    .tegel-tekst { min-width: 0; flex: 1; }
    .tegel-naam-rij { display: flex; align-items: center; gap: 4px; min-width: 0; }
    button.potlood {
      flex: none; border: none; background: none; padding: 2px; border-radius: 4px; cursor: pointer;
      color: var(--mv-text-muted); display: inline-flex;
    }
    button.potlood:hover { color: var(--mv-primary); background: var(--mv-surface-alt, #f3f4f6); }
    .tegel-naam-invoer {
      width: 100%; box-sizing: border-box; font: inherit; font-weight: 700; font-size: 0.85rem; color: var(--mv-text);
      padding: 2px 6px; border: 1px solid var(--mv-primary); border-radius: 4px; background: var(--mv-surface, #fff);
    }
    .tegel-naam { font-weight: 700; font-size: 0.85rem; color: var(--mv-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .tegel-meta { font-size: 0.72rem; color: var(--mv-text-muted); margin-top: 1px; }
    button.rond {
      flex: none; width: 28px; height: 28px; border-radius: 50%; border: none; cursor: pointer;
      background: var(--mv-surface-alt, #f3f4f6); color: var(--mv-text); display: flex; align-items: center; justify-content: center; padding: 0;
    }
    button.rond:hover, .tegel.speelt button.rond { background: var(--mv-primary); color: #fff; }
    .golf { display: flex; align-items: center; gap: 2px; height: 22px; }
    .golf span { flex: 1; background: var(--mv-border); border-radius: 1px; min-width: 2px; }
    .tegel.speelt .golf span { background: color-mix(in srgb, var(--mv-primary) 45%, transparent); }
    .tegel-voet { display: flex; align-items: center; justify-content: space-between; font-size: 0.72rem; color: var(--mv-text-muted); }
    button.link-rood { border: none; background: none; padding: 0; color: #DC2626; font: inherit; font-weight: 600; cursor: pointer; }
    button.link-rood:hover { text-decoration: underline; }

    /* Opnemen: rood. */
    .tegel.opnemen { border-color: #fca5a5; background: #fef2f2; }
    .tegel.opnemen .tegel-naam { color: #b91c1c; }
    .tegel.opnemen .golf span { background: #dc2626; }
    .tegel.opnemen .golf span.leeg { height: 2px !important; background: #fca5a5; }
    .tegel.aftellen { align-items: center; justify-content: center; text-align: center; gap: 4px; }
    .tegel.aftellen .tegel-voet { width: 100%; }
    .aftel-getal { font-size: 2.2rem; font-weight: 800; color: #dc2626; line-height: 1; animation: puls 1s infinite; }
    @keyframes puls { 0% { transform: scale(1.15); opacity: 1; } 100% { transform: scale(1); opacity: 0.75; } }
    button.rond.rec-knop { background: #fee2e2; }
    button.rond.rec-knop span { width: 10px; height: 10px; border-radius: 50%; background: #dc2626; animation: knipper 1s infinite; }
    button.rond.rec-knop:hover { background: #fecaca; }
    /* Nieuw (nog niet toegevoegd): groen, gestippeld. */
    .tegel.nieuw { border: 1.5px dashed var(--mv-primary); background: color-mix(in srgb, var(--mv-primary) 6%, var(--mv-surface, #fff)); }
    .tegel.nieuw .golf span { background: color-mix(in srgb, var(--mv-primary) 55%, transparent); }
    input.nieuw-naam {
      width: 100%; box-sizing: border-box; border: none; border-bottom: 1px solid color-mix(in srgb, var(--mv-primary) 45%, transparent);
      background: transparent; padding: 1px 0; font: inherit; font-weight: 700; font-size: 0.85rem; color: var(--mv-text); outline: none;
    }
    input.nieuw-naam:focus { border-bottom-color: var(--mv-primary); }
    .nieuw-acties { justify-content: flex-start; gap: 10px; }
    .nieuw-acties .groen { margin-left: auto; }
    button.link { border: none; background: none; padding: 0; font: inherit; font-weight: 600; color: var(--mv-text-muted); cursor: pointer; }
    button.link:hover:not(:disabled) { color: var(--mv-text); }
    button.link.groen { color: var(--mv-primary); }
    button.link.groen:hover:not(:disabled) { text-decoration: underline; color: var(--mv-primary); }
    .opname { gap: 10px; margin-top: 16px; }
    .rec { width: 12px; height: 12px; border-radius: 50%; background: #DC2626; animation: knipper 1s infinite; }
    @keyframes knipper { 50% { opacity: 0.3; } }
    .opname-tijd { font-weight: 700; color: var(--mv-text); min-width: 120px; font-size: 0.85rem; }
    .balk { flex: 1; max-width: 220px; height: 6px; border-radius: 3px; background: var(--mv-border); overflow: hidden; }
    .balk > div { height: 100%; background: #DC2626; }
    .nieuw-geluid { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 16px; }
    .nieuw-geluid input.naam {
      flex: 1; min-width: 160px; padding: 8px 12px; border: 1px solid var(--mv-border); border-radius: 6px;
      font-size: 0.85rem; background: var(--mv-surface, #fff); color: var(--mv-text); font-family: inherit;
    }
  `;
}

customElements.define('scan-sounds-card', ScanSoundsCard);
